import type { AuthError } from "@supabase/supabase-js";

/**
 * https://supabase.com/docs/guides/auth/auth-error-codes
 */
const SUPABASE_ERROR_CODES: Record<string, string> = {
	// OTP Errors
	otp_expired:
		"Der Verifizierungs-Code ist abgelaufen oder ungültig. Bitte fordere einen neuen Code an.",
	otp_disabled: "Die Anmeldung mit Verifizierungs-Code ist derzeit nicht verfügbar.",
	over_email_send_rate_limit:
		"Zu viele E-Mails wurden gesendet. Du kannst erst nach 60 Sekunden einen neuen Code anfordern.",

	// Login Errors
	invalid_credentials:
		"Ungültige Anmeldedaten. Bitte überprüfe deine E-Mail-Adresse und dein Passwort.",
	email_not_confirmed:
		"Deine E-Mail-Adresse wurde noch nicht bestätigt. Bitte überprüfe dein Postfach.",
	user_not_found: "Kein Benutzer mit dieser E-Mail-Adresse gefunden.",
	over_request_rate_limit:
		"Zu viele Anfragen. Bitte warte einen Moment, bevor du es erneut versuchst.",

	// Sign Up Errors
	email_exists: "Diese E-Mail-Adresse ist bereits registriert.",
	user_already_exists: "Ein Benutzer mit diesen Daten existiert bereits.",
	weak_password:
		"Das Passwort erfüllt nicht die Sicherheitsanforderungen. Bitte wähle ein stärkeres Passwort.",
	validation_failed: "Die eingegebenen Daten sind ungültig. Bitte überprüfe deine Eingaben.",
	signup_disabled: "Die Registrierung ist derzeit nicht möglich.",
	email_provider_disabled: "Die Registrierung per E-Mail ist derzeit nicht verfügbar.",

	// Session Errors
	session_expired: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
	session_not_found: "Deine Sitzung wurde nicht gefunden. Bitte melde dich erneut an.",
	refresh_token_not_found:
		"Deine Sitzung konnte nicht erneuert werden. Bitte melde dich erneut an.",
	refresh_token_already_used:
		"Deine Sitzung wurde bereits verwendet. Bitte melde dich erneut an.",

	// User Account Errors
	user_banned: "Dieses Benutzerkonto wurde gesperrt.",
	user_sso_managed:
		"Dieses Benutzerkonto wird über SSO verwaltet und kann nicht direkt bearbeitet werden.",

	// Email Errors
	email_address_invalid: "Die eingegebene E-Mail-Adresse ist ungültig.",
	email_address_not_authorized:
		"E-Mails können derzeit nur an Mitglieder deiner Organisation gesendet werden.",

	// Phone Errors (falls verwendet)
	phone_exists: "Diese Telefonnummer ist bereits registriert.",
	phone_not_confirmed:
		"Deine Telefonnummer wurde noch nicht bestätigt. Bitte überprüfe deine Nachrichten.",
	phone_provider_disabled: "Die Anmeldung per Telefonnummer ist derzeit nicht verfügbar.",
	over_sms_send_rate_limit:
		"Zu viele SMS-Nachrichten wurden gesendet. Bitte warte einen Moment, bevor du es erneut versuchst.",

	// OAuth Errors
	oauth_provider_not_supported: "Dieser Anmeldeanbieter wird nicht unterstützt.",
	provider_disabled: "Dieser Anmeldeanbieter ist derzeit nicht verfügbar.",
	bad_oauth_callback: "Fehler bei der Anmeldung. Bitte versuche es erneut.",
	bad_oauth_state: "Fehler bei der Anmeldung. Bitte versuche es erneut.",

	// MFA Errors
	mfa_challenge_expired:
		"Die Multi-Faktor-Authentifizierung ist abgelaufen. Bitte starte den Vorgang erneut.",
	mfa_verification_failed:
		"Die Multi-Faktor-Authentifizierung ist fehlgeschlagen. Bitte versuche es erneut.",
	mfa_factor_not_found: "Der Multi-Faktor-Authentifizierungs-Faktor wurde nicht gefunden.",
	insufficient_aal:
		"Eine höhere Authentifizierungsstufe ist erforderlich. Bitte löse eine MFA-Herausforderung.",

	// General Errors
	unexpected_failure: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es später erneut.",
	request_timeout: "Die Anfrage hat zu lange gedauert. Bitte versuche es erneut.",
	conflict: "Ein Konflikt ist aufgetreten. Bitte versuche es erneut.",
};

export const getSupabaseErrorMessage = (error: unknown): { summary: string; detail: string } => {
	if (!error || typeof error !== "object") {
		return {
			summary: "Fehler",
			detail: "Ein unbekannter Fehler ist aufgetreten",
		};
	}

	const authError = error as AuthError & { code?: string };

	if (authError.code) {
		const errorMessage = SUPABASE_ERROR_CODES[authError.code];
		if (errorMessage) {
			return {
				summary: "Fehler",
				detail: errorMessage,
			};
		}
	}

	const errorMessageText =
		authError.message || (error as Error).message || "Ein unbekannter Fehler ist aufgetreten";

	return {
		summary: "Fehler",
		detail: errorMessageText,
	};
};
